import Vue from "vue";
import RoutePathEnum from "../../enums/RoutePathEnum";
import router from "../../js/router";
import UrlMap from "../../js/slug-to-url-map";

const accessTokenKey = 'accessToken';

/**
 * Target origin for postMessage to a popup opener window.
 * Defaults to "*" until final opener URL/origin setup is in place; override via
 * VUE_APP_SSO_POST_MESSAGE_TARGET_ORIGIN when restricting to a specific origin.
 */
const ssoPostMessageTargetOrigin = process.env.VUE_APP_SSO_POST_MESSAGE_TARGET_ORIGIN || '*';

export default class AuthService {

    static getSsoPostMessageTargetOrigin() {
        return ssoPostMessageTargetOrigin;
    }

    /**
     * When SSO completes in a popup opened by another window, deliver the auth
     * payload to the opener and close this window.
     *
     * @param {object} payload Auth response (e.g. accessToken, expiresIn)
     * @returns {boolean} true when delivered to opener; caller should skip default navigation
     */
    static deliverAuthPayloadToOpener(payload) {
        if (!window.opener || window.opener.closed) {
            return false;
        }

        try {
            window.opener.postMessage(payload, this.getSsoPostMessageTargetOrigin());
        } catch (error) {
            console.error('Failed to post SSO auth payload to opener', error);
            return false;
        }

        window.close();
        return true;
    }

    /**
     * Notify the opener when SSO fails in a popup context, then close this window.
     *
     * @param {object} [errorPayload] Optional error details for the opener
     * @returns {boolean} true when delivered to opener; caller should skip default navigation
     */
    static deliverAuthFailureToOpener(errorPayload = { authError: true }) {
        if (!window.opener || window.opener.closed) {
            return false;
        }

        try {
            window.opener.postMessage(errorPayload, this.getSsoPostMessageTargetOrigin());
        } catch (error) {
            console.error('Failed to post SSO auth failure to opener', error);
            return false;
        }

        window.close();
        return true;
    }

    /**
     * Complete a successful authentication: set session, then deliver to popup
     * opener / React Native WebView, or fall back to dashboard redirect.
     *
     * @param {object} data Auth response (e.g. accessToken, expiresIn)
     * @returns {boolean} true when handed off to opener (caller should not navigate)
     */
    static completeAuthentication(data) {
        this.login(data);

        if (this.deliverAuthPayloadToOpener(data)) {
            return true;
        }

        if (typeof window.ReactNativeWebView !== 'undefined') {
            window.ReactNativeWebView.postMessage(JSON.stringify(data));
        }

        this.redirectUserToTheOldAppOrDashboard();
        return false;
    }

    /**
     * When the user already has a session and lands on a login route opened by
     * another window, send the same auth payload shape as a fresh login.
     *
     * @returns {boolean} true when handed off to opener (caller should not navigate)
     */
    static handleAlreadyAuthenticatedSession() {
        const data = this.getAuthPayloadFromSession();
        if (!data) {
            return false;
        }

        return this.deliverAuthPayloadToOpener(data);
    }

    /**
     * Build the login response payload from the current session cookie.
     *
     * @returns {{ accessToken: string, expiresIn: number }|null}
     */
    static getAuthPayloadFromSession() {
        const accessToken = this.getAccessToken();
        if (!accessToken) {
            return null;
        }

        const expiresIn = this.getAccessTokenExpiresIn(accessToken);
        if (expiresIn <= 0) {
            return null;
        }

        return { accessToken, expiresIn };
    }

    static getAccessTokenExpiresIn(accessToken) {
        try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            if (payload.exp) {
                return payload.exp - Math.floor(Date.now() / 1000);
            }
        } catch (error) {
            return 0;
        }

        return 0;
    }

    static login(data) {
        let accessToken = data.accessToken ?? '';
        let expiresIn = data.expiresIn ?? 0;

        if (accessToken.length > 0 && expiresIn > 0) {
            Vue.$cookies.set(accessTokenKey, accessToken, expiresIn, null, process.env.VUE_APP_HOST);
        }
    }

    static logout() {
        Vue.$api.logout().then(() => {
                this.revokeAccessToken()
                router.push(RoutePathEnum.LOGIN);
        })
    }

    static getAccessToken() {
        return Vue.$cookies.get(accessTokenKey);
    }

    static revokeAccessToken() {
        Vue.$cookies.remove(accessTokenKey, null, process.env.VUE_APP_HOST);
        Vue.$cookies.remove(accessTokenKey);
    }

    static isLogged() {
        return Vue.$cookies.isKey(accessTokenKey);
    }

    static redirectUserToTheOldAppOrDashboard() {
        this.loadingState = true;
        Vue.$api.authenticatedInit()
            .then(({data}) => {
                /**
                 * NAV-7620 - user should continue to be auto redirected to a platform
                 * if they only have access to one (excluding new academy)
                 */
                const access = data.user.accesses.filter(x => x.hasAccess === true && x.appSlug !== 'newAcademy');

                if (access.length !== 1) {
                    this.goToTheDashboard();
                    return;
                }

                let appSlug = access[0].appSlug;

                if (appSlug === 'academy') {
                    const migrationStatus = access[0].migrationStatus ?? 'navitas';
                    if (migrationStatus === 'calibrae') {
                        appSlug = 'newAcademy';
                    }
                }

                if (appSlug in UrlMap) {
                    window.location = UrlMap[appSlug];
                    return;
                }

                this.goToTheDashboard();
            }).catch(() => {
            this.goToTheDashboard();
        }).finally(() => {
            this.loadingState = false;
        });
    }

    static goToTheDashboard() {
        /* Delay for setting cookies */
        setTimeout(() => {
            router.push(RoutePathEnum.DASHBOARD);
        }, 10);
    }
}
